import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Envelope, Lock } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import LanguageSwitch from '../components/LanguageSwitch';
import SplashIntro from '../components/SplashIntro';
import SocialAuthRow from '../components/SocialAuthRow';
import WeeklyWashCounter from '../components/WeeklyWashCounter';
import RecoveryDialog from '../components/RecoveryDialog';
import { BRAND, BRAND_ASSETS } from '../config/programs';

export default function LoginPage() {
  const { login, loginWithGoogle } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(location.state?.error || '');
  const [loading, setLoading] = useState(false);
  const [recoverOpen, setRecoverOpen] = useState(false);
  // Splash key bumped on every Hyr ↔ Regjistrohu switch to force a replay.
  const [splashKey, setSplashKey] = useState(() => Date.now());
  const [splashDone, setSplashDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
  };

  const onSwitchToRegister = () => {
    // Replay the splash on switch — clear gate and remount with a fresh key.
    try { sessionStorage.removeItem('autospa5-splash-played'); } catch (err) { console.debug('[Login] sessionStorage unavailable', err?.message); }
    setSplashDone(false);
    setSplashKey(Date.now());
    navigate('/register');
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {!splashDone && (
        <SplashIntro key={splashKey} force onDone={() => setSplashDone(true)} />
      )}

      {/* Header — only logo, no centered duplicate below */}
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-3">
          <div className="relative w-12 h-12 flex items-center justify-center">
            <div className="absolute inset-[-50%] rounded-full blur-2xl pointer-events-none"
                 style={{ background: `radial-gradient(circle, ${BRAND.primary} 0%, ${BRAND.primaryGlow} 40%, transparent 70%)`, opacity: 0.85 }} />
            <img src={BRAND_ASSETS.logo} alt="AutoSpa5"
                 className="w-12 h-12 relative z-10 object-contain"
                 style={{ filter: `drop-shadow(0 0 8px ${BRAND.primary}) drop-shadow(0 0 20px ${BRAND.primary}) drop-shadow(0 0 36px ${BRAND.primaryGlow})` }} />
          </div>
          <span className="text-xl font-semibold tracking-tight font-['Outfit']">AutoSpa5</span>
        </div>
        <LanguageSwitch />
      </div>

      {/* Content */}
      <motion.div
        className="flex-1 flex flex-col justify-center px-6 pb-8"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: splashDone ? 1 : 0, y: splashDone ? 0 : 24 }}
        transition={{ duration: 0.5, delay: 0.25 }}
      >
        <div className="max-w-sm mx-auto w-full">
          <h1 className="text-3xl font-semibold tracking-tight mb-2 font-['Outfit']">{t('login')}</h1>
          <p className="text-[#0ABAB5] font-medium mb-1">{t('mainSlogan')}</p>
          <p className="text-white/50 text-sm">{t('washIn5Min')}</p>

          {/* Live social-proof counter (matches HomePage) */}
          <WeeklyWashCounter />

          <form onSubmit={handleSubmit} className="space-y-4 mt-7">
            {error && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <div className="relative">
              <Envelope size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
              <Input
                type="email"
                placeholder={t('email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-14 bg-[#121212] border-white/10 pl-12 rounded-xl text-white placeholder:text-white/30 focus:border-[#0ABAB5] focus:ring-[#0ABAB5]"
                data-testid="login-email"
                required
              />
            </div>

            <div className="relative">
              <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
              <Input
                type="password"
                placeholder={t('password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-14 bg-[#121212] border-white/10 pl-12 rounded-xl text-white placeholder:text-white/30 focus:border-[#0ABAB5] focus:ring-[#0ABAB5]"
                data-testid="login-password"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="relative w-full h-14 cta-button-orange font-semibold rounded-2xl"
              data-testid="login-submit"
            >
              {loading ? t('loading') : t('signIn')}
            </Button>

            <button
              type="button"
              onClick={() => setRecoverOpen(true)}
              className="block w-full text-center text-xs text-white/45 hover:text-[#0ABAB5] transition mt-1"
              data-testid="forgot-password-link"
            >
              {t('forgotPassword')}
            </button>
          </form>

          <RecoveryDialog open={recoverOpen} onClose={() => setRecoverOpen(false)} />

          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-white/40 text-sm">{t('continueWith')}</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <SocialAuthRow
            onGoogle={loginWithGoogle}
            onMock={(p) => setError(`${p} login is not yet available. Please use email or Google.`)}
          />

          <p className="mt-8 text-center text-white/50">
            {t('noAccount')}{' '}
            <Link
              to="/register"
              onClick={(e) => { e.preventDefault(); onSwitchToRegister(); }}
              className="text-[#0ABAB5] font-medium"
              data-testid="register-link"
            >
              {t('signUp')}
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
